import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import deepClone from "@utils/deepClone";
import { UNCATEGORIZED_ID } from "../../constants";
import { useTranslation } from "react-i18next";
import { clear, initCategoryData, updateFilter, changeFold } from "@modules/categoryModalTreeView";
import { clearCategoryGroup, getCategoryGroups } from "@modules/categoryGroupsManagement";
import WithSelect from "@components/select";
import fp from "lodash/fp";
import {
    IconCancelCircle,
    IconHierarchyFolded,
    IconHierarchyOpened,
    IconRefresh,
    IconResetSmall,
    IconSearch,
    ImageCategoryGroupOn,
    ImageCategorySelect,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    Tooltip,
} from "@mzc-pdc/ui";
import useActionPending from "@hooks/useActionPending";
import StyledCategoryTreeDiv from "./categories-input.styled";
import AdaptivePopper from "@components_v2/adaptive-popper";

const SelectCategoryGroupPopper = ({
    isMultiple = true,
    disablePopper = false,
    project,
    selectedCategoryGroup,
    selectedCategories,
    type,
    defaultCategoryGroup,
    onChangeCategories,
    isCheckedFirstCategory = false,
    onModalClose,
    onClose,
    submitText,
    anchorEl,
    placement,
    open,
    disablePortal,
    wrapperSx = {},
    hiddenSummary = false,
    maxCount,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchKeyword, setSearchKeyword] = useState("");
    const [selectedCategories_, setSelectedCategories_] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categoryGroup, setCategoryGroup] = useState({ value: "", label: "" });
    const { pending, setActionPending, disableActionPending } = useActionPending("categoryModalTreeView");
    const categoryGroups = useSelector(({ categoryGroupsManagement }) => categoryGroupsManagement.categoryGroups);
    const categoryModalTreeView = useSelector(({ categoryModalTreeView }) => categoryModalTreeView);

    const onRefresh = async () => {
        try {
            setActionPending();
            setSelectedCategories_([]); // 선택된 카테고리
            if (disablePopper) {
                let selectedCategoryGroupInfo = {
                    categoryGroup: { id: undefined, label: undefined },
                    categories: [],
                };
                onChangeCategories(selectedCategoryGroupInfo);
            }
            await initData();
            onClickCategoryTreeFilter();
        } catch (e) {
            console.error(e);
        } finally {
            disableActionPending();
        }
    };

    const initData = async () => {
        await dispatch(clear());

        if (categoryGroup.value) {
            const response = await dispatch(initCategoryData(project.id, type, categoryGroup.value));

            await dispatch(
                getCategoryGroups(project.id, {
                    name: "",
                    creator: "",
                    status: "ACTIVE",
                }),
            );

            const firstCategory = response.data?.[0]?.children?.[0];
            if (firstCategory && isCheckedFirstCategory) {
                setSelectedCategories_([firstCategory]);
                onChangeCategories({
                    categoryGroup: { id: categoryGroup.value, label: categoryGroup.label },
                    categories: [
                        {
                            value: firstCategory.id,
                            label: firstCategory.name,
                            name: firstCategory.name,
                            id: firstCategory.id,
                        },
                    ],
                });
            }
        } else {
            await dispatch(
                getCategoryGroups(project.id, {
                    name: "",
                    creator: "",
                    status: "ACTIVE",
                }),
            );
        }
    };

    const init = async () => {
        try {
            setActionPending();
            await initData();
        } catch (e) {
            console.error(e);
        } finally {
            disableActionPending();
        }
    };

    useEffect(() => {
        if (selectedCategories?.length > 0) {
            setSelectedCategories_(selectedCategories);
        }
        if (selectedCategoryGroup?.id) {
            setCategoryGroup({
                label: selectedCategoryGroup?.id,
                value: selectedCategoryGroup.value,
            });
        }
        return () => {
            setCategoryGroup({ value: null });
            dispatch(clearCategoryGroup());
            dispatch(clear());
        };
    }, []);

    useEffect(() => {
        if (defaultCategoryGroup) {
            setCategoryGroup({ value: defaultCategoryGroup.id });
        }
    }, [defaultCategoryGroup]);

    useEffect(() => {
        const categories =
            selectedCategories?.map((v) => {
                return {
                    ...v,
                    id: `${v.id}`,
                };
            }) ?? [];

        setSelectedCategories_(categories);
    }, [selectedCategories]);

    useEffect(() => {
        dispatch(clear());
    }, [project]);

    useEffect(() => {
        init();
    }, [project, categoryGroup?.value]);

    const onSelectedCategories = () => {
        let selectedCategoryGroupInfo = {
            categoryGroup: { id: categoryGroup.value, label: categoryGroup.label },
            categories: selectedCategories_.map((v) => {
                return {
                    value: v.id,
                    label: v.name,
                    name: v.name,
                    id: v.id,
                };
            }),
        };
        onChangeCategories(selectedCategoryGroupInfo);
    };

    const onClickClose = () => {
        dispatch(clear());

        onModalClose(false);
        onClose();
    };

    const onChangeSearchKeyword = (keyword) => {
        setSearchKeyword(keyword);
    };

    const getCategoryGroupLabel = useCallback(
        (id) => {
            return categoryGroups.find((el) => el.id === id)?.name;
        },
        [categoryGroup, categoryGroups],
    );

    function makeMultipleDiv(item) {
        if (!item?.code) {
            return "";
        }
        const makeArray = item.code.split("_").splice(1) || [];
        if (makeArray.length < 1) {
            return "";
        }
        return makeArray.map((item, index) => {
            return <span key={index} />;
        });
    }

    const ChildItem = useCallback(
        (child) => {
            const childItems = child.map((item) => {
                return (
                    <React.Fragment key={item.id}>
                        <ul>
                            <li
                                className={`${selectedCategories_.find((v) => v.id == item.id) ? "tree-selected" : ""}`}
                            >
                                <div
                                    className={`tree-item ${
                                        selectedCategories_.find((v) => v.id == item.id) ? "tree-item-selected" : ""
                                    }`}
                                    style={{ width: "100%" }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (
                                            !selectedCategories_.some((v) => v.id === item.id) &&
                                            maxCount &&
                                            selectedCategories_.length >= maxCount
                                        )
                                            return;
                                        if (isMultiple)
                                            onChangeCategoryMultiChecked(
                                                item,
                                                !selectedCategories_?.find((v) => v.id == item.id),
                                            );
                                        else onChangeCategorySingleChecked(item);
                                    }}
                                >
                                    {item && makeMultipleDiv(item)}
                                    <button
                                        type="button"
                                        className={`btn btn-fold btn-icon-solid ${!item.isFold ? "active" : ""} ${
                                            item.children.length === 0 ? "disabled" : ""
                                        }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onClickFoldButton(item);
                                        }}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        {item.isFold ? (
                                            <IconHierarchyFolded
                                                size={12}
                                                htmlColor={item.children.length === 0 ? `#6f6f8366` : `#6f6f83`}
                                            />
                                        ) : (
                                            <IconHierarchyOpened
                                                size={12}
                                                htmlColor={item.children.length === 0 ? `#6f6f8366` : `#6f6f83`}
                                            />
                                        )}
                                    </button>
                                    <Box
                                        sx={{
                                            display: "flex",

                                            alignItems: "center",
                                            flex: 1,
                                        }}
                                    >
                                        {isMultiple && (
                                            <Checkbox
                                                color={"greySecondary"}
                                                indeterminate={item.checkState === "Indeterminate"}
                                                checked={
                                                    selectedCategories_.length > 0 &&
                                                    selectedCategories_.find((v) => v.id == item.id) !== undefined
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    onChangeCategoryMultiChecked(item, e.target.checked);
                                                }}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                disabled={
                                                    !selectedCategories_.some((v) => v.id === item.id) &&
                                                    maxCount &&
                                                    selectedCategories_.length >= maxCount
                                                }
                                            />
                                        )}
                                        <label className="tree-item-label" style={{ marginRight: 0, borderRadius: 0 }}>
                                            <i className={"input-helper"} />
                                            <span>{item.name}</span>
                                        </label>
                                    </Box>
                                </div>
                                {item && !item.isFold && true && item.children.length > 0
                                    ? ChildItem(item.children)
                                    : ""}
                            </li>
                        </ul>
                    </React.Fragment>
                );
            });
            return childItems;
        },
        [categoryModalTreeView, selectedCategories_],
    );

    const treeData = useMemo(() => {
        const isFilter = categoryModalTreeView?.filter?.isFilter;
        const categoryGroup_ = isFilter
            ? categoryModalTreeView?.filter?.data?.find((v) => v.id !== UNCATEGORIZED_ID)
            : categoryModalTreeView?.data?.find((v) => v.id !== UNCATEGORIZED_ID);
        return categoryGroup_ && categoryGroup_.children;
    }, [categoryModalTreeView, selectedCategories_]);

    const renderTree = useCallback(() => {
        const treeData_ = treeData;
        if (!treeData_) return <React.Fragment />;
        const listItems = treeData_?.map((tree) => (
            <React.Fragment key={tree.id}>
                <ul>
                    <li className={`${selectedCategories_.find((v) => v.id == tree.id) ? "tree-selected" : ""}`}>
                        <div
                            className={`tree-item ${
                                selectedCategories_.find((v) => v.id == tree.id) ? "tree-item-selected" : ""
                            }`}
                            style={{ width: "100%" }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (
                                    !selectedCategories_.some((v) => v.id === tree.id) &&
                                    maxCount &&
                                    selectedCategories_.length >= maxCount
                                )
                                    return;
                                if (isMultiple)
                                    onChangeCategoryMultiChecked(
                                        tree,
                                        !selectedCategories_?.find((v) => v.id == tree.id),
                                    );
                                else onChangeCategorySingleChecked(tree);
                            }}
                        >
                            {tree && makeMultipleDiv(tree)}
                            <button
                                type="button"
                                className={`btn btn-icon-solid btn-fold ${!tree.isFold ? "active" : ""} ${
                                    tree.children.length === 0 ? "disabled" : ""
                                }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClickFoldButton(tree);
                                }}
                                onChange={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                {tree.isFold ? (
                                    <IconHierarchyFolded
                                        size={12}
                                        htmlColor={tree.children.length === 0 ? `#6f6f8366` : `#6f6f83`}
                                    />
                                ) : (
                                    <IconHierarchyOpened
                                        size={12}
                                        htmlColor={tree.children.length === 0 ? `#6f6f8366` : `#6f6f83`}
                                    />
                                )}
                            </button>
                            <Box
                                sx={{
                                    display: "flex",

                                    alignItems: "center",
                                    flex: 1,
                                }}
                            >
                                {isMultiple && (
                                    <Checkbox
                                        color={"greySecondary"}
                                        indeterminate={tree.checkState === "Indeterminate"}
                                        checked={
                                            selectedCategories_.length > 0 &&
                                            selectedCategories_.find((v) => v.id == tree.id)
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onChangeCategoryMultiChecked(tree, e.target.checked);
                                        }}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                        }}
                                        disabled={
                                            !selectedCategories_.some((v) => v.id === tree.id) &&
                                            maxCount &&
                                            selectedCategories_.length >= maxCount
                                        }
                                    />
                                )}
                                <label className={"tree-item-label"} style={{ marginRight: 0, borderRadius: 0 }}>
                                    <i className={"input-helper"} />
                                    <span>{tree.name}</span>
                                </label>
                            </Box>
                        </div>
                        {tree && !tree.isFold && true && tree.children.length > 0 ? ChildItem(tree.children) : ""}
                    </li>
                </ul>
            </React.Fragment>
        ));

        return listItems;
    }, [categoryModalTreeView, selectedCategories_]);

    const onClickFoldButton = (item) => {
        if (item.isGroup) return;

        item.isFold = !item.isFold;

        if (item.children && !item.children.length) {
            item.isEmpty = true;
        }

        dispatch(changeFold(item));
    };

    const onChangeCategorySingleChecked = useCallback(
        (category) => {
            const categories = category ? [{ ...category, label: category.name }] : [];
            setSelectedCategories_(categories);
            if (disablePopper) {
                let selectedCategoryGroupInfo = {
                    categoryGroup: { id: categoryGroup.value, label: categoryGroup.label },
                    categories: [
                        {
                            value: category?.id,
                            label: category?.name,
                            name: category?.name,
                            id: category?.id,
                        },
                    ],
                };
                onChangeCategories(selectedCategoryGroupInfo);
            }
        },
        [categoryGroup, selectedCategories_, selectedCategoryGroup],
    );

    const onChangeCategoryMultiChecked = useCallback(
        (category, checkValue) => {
            let categories = [];
            if (checkValue === true) categories = [...selectedCategories_, { ...category, label: category.name }];
            else categories = selectedCategories_.filter((v) => v.id != category.id);

            setSelectedCategories_(categories);

            if (disablePopper) {
                let selectedCategoryGroupInfo = {
                    categoryGroup: { id: categoryGroup.value, label: categoryGroup.label },
                    categories: categories.map((v) => {
                        return {
                            value: v.id,
                            label: v.name,
                            name: v.name,
                            id: v.id,
                        };
                    }),
                };
                onChangeCategories(selectedCategoryGroupInfo);
            }
        },
        [categoryGroup, selectedCategories_],
    );

    const onApplyCategoryGroup = (option) => {
        onClickResetCategoryTreeFilter();

        setSelectedCategories_([]);
        setCategoryGroup(option || { value: "", label: "" });
    };

    const onClearCategories = () => {
        if (disablePopper) {
            let selectedCategoryGroupInfo = {
                categoryGroup: { id: undefined, label: undefined },
                categories: [],
            };
            onChangeCategories(selectedCategoryGroupInfo);
        }
        setSelectedCategories_([]);
    };

    const onClickResetCategoryTreeFilter = () => {
        dispatch(
            updateFilter({
                isFilter: false,
                data: [],
            }),
        );

        setSearchKeyword("");
    };

    const onClickCategoryTreeFilter = useCallback(() => {
        const keyword = searchKeyword;

        if (!keyword) onClickResetCategoryTreeFilter();

        const filteredData = deepClone(categoryModalTreeView.data).filter(function f(o) {
            o.isFold = false;
            o.isEmpty = true;

            if (o.name.toUpperCase().includes(keyword.toUpperCase())) {
                o.isEmpty = false;
                o.isFold = false;
                return true;
            }
            return o.children && (o.children = o.children.filter(f)).length > 0;
        });

        dispatch(
            updateFilter({
                isFilter: true,
                data: filteredData || [],
            }),
        );
    }, [categoryModalTreeView, searchKeyword]);

    const HeaderComponent = useMemo(
        () => (
            <>
                <Box p={2}>
                    {defaultCategoryGroup?.id ? (
                        <Grid container flexDirection={"row"} gap={1} alignItems={"center"}>
                            <ImageCategoryGroupOn width={16} height={16} />

                            <Typography variant={"body1_500"}>{getCategoryGroupLabel(categoryGroup?.value)}</Typography>
                        </Grid>
                    ) : (
                        <Grid container flexDirection={"row"} gap={1} alignItems={"center"}>
                            <ImageCategoryGroupOn width={16} height={16} />
                            <WithSelect
                                options={categoryGroups.map((category) => {
                                    return {
                                        label: category.name,
                                        value: category.id,
                                    };
                                })}
                                disabled={categoryModalTreeView.pending}
                                isSearchable={false}
                                isClearable={true}
                                placeholder={t(`common::label::${"Search Category Group"}`)}
                                defaultValue={categoryGroup?.value || ""}
                                onChange={(option) => {
                                    onApplyCategoryGroup(option);
                                }}
                            />
                        </Grid>
                    )}
                </Box>
                {categoryGroup.value && (
                    <Grid
                        container
                        width={1}
                        alignItems={"center"}
                        pt={1.5}
                        pb={2}
                        pl={disablePopper ? 0 : 2}
                        gap={1.5}
                        borderBottom={1}
                        borderColor={"divider"}
                    >
                        <Grid item xs>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClickCategoryTreeFilter();
                                }}
                            >
                                <TextField
                                    value={searchKeyword}
                                    sx={{ width: "100%" }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconSearch size={16} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={t(`common::label::Clear`, `Clear`)}>
                                                    <IconButton
                                                        circled
                                                        onClick={(e) => {
                                                            onClickResetCategoryTreeFilter();
                                                        }}
                                                        sx={{
                                                            opacity: searchKeyword ? 1 : 0,
                                                        }}
                                                    >
                                                        <IconCancelCircle
                                                            size={16}
                                                            color={theme.palette.greySecondary[400]}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder={t(`common::msg::Search by category name`, "Search by category name")}
                                    disabled={fp.isEmpty(categoryGroup)}
                                    onChange={(e) => {
                                        onChangeSearchKeyword(e.target.value);
                                    }}
                                    onBlur={(e) => {
                                        onChangeSearchKeyword(e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onClickCategoryTreeFilter();
                                        }
                                    }}
                                />
                            </form>
                        </Grid>
                        <Tooltip title={t(`common::label::Refresh`, `Refresh`)}>
                            <IconButton
                                circled
                                onClick={(e) => {
                                    onRefresh();
                                }}
                            >
                                <IconRefresh size={16} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                )}
            </>
        ),
        [
            defaultCategoryGroup?.id,
            getCategoryGroupLabel,
            categoryGroup?.value,
            categoryGroups,
            categoryModalTreeView.pending,
            t,
            onApplyCategoryGroup,
        ],
    );

    const renderTreeContent = useCallback(() => {
        if (categoryModalTreeView.pending || pending) {
            return (
                <div className="tree-view">
                    <div className={"loading-block"}>
                        <span className={"loading-content text-primary"} />
                    </div>
                </div>
            );
        }

        if (fp.isEmpty(categoryGroup.value)) {
            return (
                <Stack p={"30px"} justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
                    <i className="sprite">
                        <ImageCategorySelect width={76} height={60} />
                    </i>
                    <p className={`text-center`}>
                        {t(
                            "common::msg::Select a category from the category group.",
                            "Select a category from the category group.",
                        )}
                    </p>
                </Stack>
            );
        }

        if (treeData && treeData.length > 0) {
            return (
                <div className="tree-view">
                    <StyledCategoryTreeDiv className="tree-select">{renderTree()}</StyledCategoryTreeDiv>
                </div>
            );
        }

        if (!categoryModalTreeView.pending && searchKeyword && !fp.isEmpty(categoryGroup.value)) {
            return (
                <div className={"empty"}>
                    {t(`common::msg::${"No {{name}} searched."}`, {
                        name: t(`common::label::${"Categories"}`),
                    })}
                </div>
            );
        }

        return <div className={"empty"} />;
    }, [categoryModalTreeView.pending, pending, categoryGroup.value, treeData, searchKeyword, renderTree, t]);

    const FooterComponent = useMemo(
        () => (
            <>
                {!hiddenSummary && (
                    <Stack direction={"column"} gap={1.5} px={2} py={1.5} pb={disablePopper ? 1.5 : 0}>
                        <Grid container flexDirection={"column"} gap={2} alignItems={"center"}>
                            <Grid item sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <Grid item flex={1}>
                                    <Typography variant={"body3"}>
                                        {t(`common::label::${"Categories selected"}`)}
                                    </Typography>
                                    {maxCount && selectedCategories_.length > 0 && (
                                        <Typography component="span" display="inline" variant={"body3_400"}>
                                            {" "}
                                            (
                                            <Typography
                                                component="span"
                                                color={theme.palette.primary.main}
                                                display="inline"
                                                variant={"body3_400"}
                                            >
                                                {selectedCategories_ && selectedCategories_.length}
                                            </Typography>
                                            /{maxCount})
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Button
                                        size={"xsmall"}
                                        variant={"text"}
                                        disabled={selectedCategories_.length === 0}
                                        startIcon={<IconResetSmall size={16} />}
                                        onClick={onClearCategories}
                                    >
                                        {t(`common::label::${"Clear All"}`)}
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid item sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <Grid container gap={0.5}>
                                    {selectedCategories_ &&
                                        selectedCategories_.map((category, index) => (
                                            <Chip
                                                size={"small"}
                                                key={category.id}
                                                label={category.label}
                                                onDelete={(e) => {
                                                    e.stopPropagation();
                                                    if (isMultiple) onChangeCategoryMultiChecked(category, false);
                                                    else onChangeCategorySingleChecked();
                                                }}
                                                sx={{
                                                    maxWidth: 200,
                                                }}
                                            />
                                        ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Stack>
                )}
                {!disablePopper && (
                    <Stack gap={1.5} px={2} pb={1.5}>
                        {!hiddenSummary && <Divider />}
                        <Grid container mt={0.5} justifyContent={"flex-end"}>
                            <Button
                                variant={"contained"}
                                size={"small"}
                                color={"greySecondary"}
                                loading={isSubmitting}
                                disabled={selectedCategories_ && selectedCategories_.length <= 0}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsSubmitting(true);
                                    await onSelectedCategories();
                                    onClickClose();
                                }}
                            >
                                {submitText ? t(`common::label::${submitText}`) : t(`common::label::${"Select"}`)}
                            </Button>
                        </Grid>
                    </Stack>
                )}
            </>
        ),
        [
            hiddenSummary,
            disablePopper,
            selectedCategories_,
            maxCount,
            t,
            onClearCategories,
            isMultiple,
            onChangeCategoryMultiChecked,
            onChangeCategorySingleChecked,
            isSubmitting,
            submitText,
            onSelectedCategories,
            onClickClose,
            setIsSubmitting,
        ],
    );

    if (open !== undefined && !open) return null;

    if (!disablePopper) {
        return (
            <AdaptivePopper
                open={open}
                anchorEl={anchorEl}
                placement={placement}
                disablePortal={disablePortal}
                wrapperSx={wrapperSx}
                minHeight={300}
                maxHeight={600}
            >
                <Box data-role="header">{HeaderComponent}</Box>
                <Stack sx={{ height: "100%" }} data-role="content">
                    {renderTreeContent()}
                </Stack>
                <Box data-role="footer">{FooterComponent}</Box>
            </AdaptivePopper>
        );
    }

    return (
        <>
            <Box data-role="header" sx={{ flexShrink: 0 }}>
                {HeaderComponent}
            </Box>
            <Box
                data-role="content"
                className={`${fp.isEmpty(categoryGroup.value) && "tree-guide"}`}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >
                {renderTreeContent()}
            </Box>
            <Box data-role="footer">{FooterComponent}</Box>
        </>
    );
};

export default SelectCategoryGroupPopper;
